import { useState } from "react";
import axios from "axios";
import {
  Button,
  OutlineA,
  SubtleIconA,
  SubtleIconButton,
} from "components/button";
import Field from "components/field";
import { StyledLabel } from "components/input/style";
import Assignee from "components/react-select/assignees";
import SafeArea from "components/safe-area";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import { useFormik } from "formik";
import Switch from "rc-switch";
import * as React from "react";
import { RiCloseLine } from "react-icons/ri";
import { useMutation, useQuery } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { TCustomDoc, TCustomDocForm } from "types/custom-doc";
import * as Yup from "yup";
import { SliderPicker } from "react-color";
import { RiAddCircleLine } from "react-icons/ri";
import { FaArrowCircleUp, FaArrowCircleDown, FaInfoCircle } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import DatePicker from "react-multi-date-picker";
import { parseISO, startOfDay, format } from 'date-fns';
import TooltippedElement from "components/TooltippedElement";

export default function CustomDocument() {
  const { t } = useTranslation();
  const tBase = "views.custom_document.index2";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const docTypes = [
    { value: "estimate", label: tr("Estimate") },
    { value: "invoice", label: tr("Invoice") },
  ];

  const [, setColorMsg] = useState<string>("");
  const [lastColor, setLastColor] = useState<string | null>(null);
  const params = useParams<{ id: string }>(),
    history = useHistory(),
    snackbar = useSnackbarContext();

  const query = useQuery<TCustomDoc>(
    ["custom document", params.id],
    async () =>
      params.id !== "new" &&
      (await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/custom-doc/${params.id}
  `
        )
        .then((res) => res.data)),
    {
      refetchOnWindowFocus: false,
      onSuccess: () => {
        formik.resetForm();
      },
    }
  );

  const mutation = useMutation(
    async (e: TCustomDocForm) =>
      await axios
        .post(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/eserv-business/custom-doc/${params.id}
  `,
          e
        )
        .then((res) => res.data),
    {
      onSuccess: (id) => {
        query.refetch();
        snackbar.showSnackbar(
          params.id === "new"
            ? tr("Custom document created!")
            : tr("Custom document updated!")
        );
        history.push(`/custom-docs/${id}`);
      },
    }
  );

  const initialValues: TCustomDocForm = {
      label: query.data?.label ?? "",
      products: query.data?.products ?? true,
      services: query.data?.services ?? true,
      terms_conditions: query.data?.terms_conditions ?? "",
      additional_terms: query.data?.additional_terms ?? [],
      type: query.data?.type ?? "estimate",
      days_until_due: query.data?.days_until_due ?? null,
      total_only: query.data?.total_only ?? false,
      checklists: query.data?.checklists || [],
      long_description: query.data?.long_description ?? true,
      short_description: query.data?.short_description ?? true,
      name: query.data?.name ?? true,
      assignees: [1],
      color: query.data?.color ?? null,
      include_images: query.data?.include_images ?? false,
      include_qty: query.data?.include_qty ?? false,
      include_line_total: query.data?.include_line_total ?? false,
    },
    onSubmit = (e: TCustomDocForm) => {
      mutation.mutate(e);
    },
    validationSchema = Yup.object({
      label: Yup.string().required(tr("Required")),
      type: Yup.string().required(tr("Required")),
      products: Yup.boolean().required(tr("Required")),
      services: Yup.boolean().required(tr("Required")),
      name: Yup.boolean().required(tr("Required")),
      terms_conditions: Yup.string().nullable(),
      additional_terms: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(tr("Required")),
          label: Yup.string().required(tr("Required")),
          text: Yup.string().required(tr("Required")),
          type: Yup.string().required(tr("Required")).oneOf([
            "none",
            "yes_no",
            "initials",
            "yes_no_initials",
          ] as const),
          // order: Yup.number().optional(),
        })
      ),
      days_until_due: Yup.number().min(0).nullable(),
      total_only: Yup.boolean(),
      checklists: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().required(tr("Required")),
          label: Yup.string().required(tr("Required")),
        })
      ),
      long_description: Yup.boolean().required(tr("Required")),
      short_description: Yup.boolean().required(tr("Required")),
      color: Yup.string().nullable(),
      include_images: Yup.boolean().required(tr("Required")),
      include_qty: Yup.boolean().required(tr("Required")),
      include_line_total: Yup.boolean().required(tr("Required")),
    });

  const formik = useFormik<TCustomDocForm>({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true,
  });

  if (query.isLoading) return <Spinner />;

  const populateChecklists = formik.values.checklists?.map((checklist, i) => {
    return (
      <div key={i} className="flex">
        <Field
          value={checklist.label}
          name={`checklists.${i}.label`}
          formik={formik}
          className="flex-1"
        />
        <SubtleIconButton
          className="!text-red-600"
          onClick={() => {
            let temp = [...formik.values.checklists!];
            temp.splice(i, 1);
            formik.setFieldValue("checklists", temp);
          }}
        >
          <RiCloseLine />
        </SubtleIconButton>
      </div>
    );
  });

  const additionalTermTypes = [
    { value: "none", label: tr("None") },
    { value: "yes_no", label: tr("Yes / No") },
    { value: "initials", label: tr("Initials") },
    { value: "yes_no_initials", label: tr("Yes / No + Initials") },
  ];

  const populateAdditionalTerms = formik.values.additional_terms?.map((term, i) => {
    return (
      <div key={i} className="space-y-1 border border-gray-300 p-2 rounded-md bg-slate-50">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            <Field
              value={term.label}
              name={`additional_terms.${i}.label`}
              formik={formik}
              label={tr("Label")}
              className="w-[200px]"
            />
          <Field
            type="select"
            value={additionalTermTypes.find((option) => option.value === term.type)}
            name={`additional_terms.${i}.type`}
            formik={formik}
            label={tr("Type")}
            className="w-[200px]"
            options={additionalTermTypes}
            onChange={(selectedOption: any) => {
              formik.setFieldValue(`additional_terms.${i}.type`, selectedOption.value);
            }}
          />
          </div>
          <div className="flex items-center">
            <button
              type="button"
              className={`
                text-gray-500 rounded-full py-0.5 pl-0.5 pr-[1.9px] 
                ${i === 0 ? "opacity-50" : "hover:text-white hover:bg-gray-500 active:text-gray-100 hover:shadow-md active:shadow-none"}
              `}
              onClick={() => {
                if (i > 0) {
                  const temp = [...formik.values.additional_terms];
                  [temp[i], temp[i - 1]] = [temp[i - 1], temp[i]];
                  formik.setFieldValue("additional_terms", temp);
                }
              }}
              disabled={i === 0}
            >
              <FaArrowCircleUp size={24} />
            </button>
            <button
              type="button"
              className={`ml-1 mr-6
                text-gray-500 rounded-full py-0.5 pl-0.5 pr-[1.9px] 
                ${i === formik.values.additional_terms.length - 1 ? "opacity-50" : "hover:text-white hover:bg-gray-500 active:text-gray-100 hover:shadow-md active:shadow-none"}
              `}
              onClick={() => {
                if (i < formik.values.additional_terms.length - 1) {
                  const temp = [...formik.values.additional_terms];
                  [temp[i], temp[i + 1]] = [temp[i + 1], temp[i]];
                  formik.setFieldValue("additional_terms", temp);
                }
              }}
              disabled={i === formik.values.additional_terms.length - 1}
            >
              <FaArrowCircleDown size={24} />
            </button>
            <button
              className="text-red-600 hover:scale-[1.1]"
              type="button"
              onClick={() => {
                let temp = [...formik.values.additional_terms!];
                temp.splice(i, 1);
                formik.setFieldValue("additional_terms", temp);
              }}
            >
              <RiCloseLine size={24} />
            </button>
          </div>
        </div>
        <Field
          type="textarea"
          value={term.text}
          name={`additional_terms.${i}.text`}
          formik={formik}
          label={tr("Text")}
          minRows={2}
        />
      </div>
    );
  });

  const handleColorChange = (color: any) => {
    formik.setFieldValue("color", color.hex);
    setLastColor(color.hex); // Update lastColor only when a color is picked
    setColorMsg("");
  };

  const handleDefaultColorSelection = (e: any) => {
    const useDefault = e.target.checked;
    if (useDefault) {
      formik.setFieldValue("color", null);
      setColorMsg(tr("You have selected the default color"));
    } else if (lastColor) {
      formik.setFieldValue("color", lastColor); // Restore the last selected color
      setColorMsg("");
    }
  };

  return (
    <div className="flex flex-col mb-16">
      <div className="sticky border-b top-0 height-[50px] z-50 bg-white flex p-2 justify-between items-center">
        <div className="space-x-4 flex items-center">
          <SubtleIconA to="/admin/custom-docs">
            <RiCloseLine />
          </SubtleIconA>
          <span>
            {params.id === "new"
              ? tr("Create Custom Document")
              : `${tr("Edit Custom Document")} #${params.id}`}
          </span>
        </div>

        <div className="space-x-2">
          <Button
            primary
            type="submit"
            isLoading={mutation.isLoading}
            onClick={() => formik.handleSubmit()}
            disabled={!formik.dirty || !formik.isValid}
          >
            {params.id === "new"
              ? tr("Create Custom Document")
              : tr("Save Changes")}
          </Button>
          <OutlineA primary to="/admin/custom-docs">
            {tr("Cancel")}
          </OutlineA>
        </div>
      </div>
      <SafeArea>
        <form onSubmit={formik.handleSubmit} className="space-y-4 py-4">
          <Field
            label={tr("Label")}
            name="label"
            type="input"
            formik={formik}
          />
          <Field
            type="select"
            label={tr("Type")}
            onChange={(e: any) => {
              formik.setValues((v) => ({ ...v, type: e.value }));
            }}
            value={docTypes.find((doc) => doc.value === formik.values.type)}
            name="type"
            options={docTypes}
          />
          {formik.values.type === "invoice" && (
            <div>
              <div className="flex items-center space-x-2">
                <div>{tr("Days Until Due")}</div>
                <TooltippedElement
                  message={tr("Number of days until the invoice is due from send date")}
                  placement="top"
                  element={<FaInfoCircle className="text-secondary" />}
                />
              </div>
              <div className="flex items-center space-x-4 mt-2 -mb-2">
                  <div className="flex flex-col items-center">
                    <div className="text-xs text-gray-600">
                      {formik.values.days_until_due !== null ? (
                        <span>{tr("On")}</span>
                      ) : (
                        <span>{tr("Off")}</span>
                      )}
                    </div>
                    <Switch
                      checked={formik.values.days_until_due !== null}
                      onChange={() => {
                        formik.setFieldValue(
                          "days_until_due",
                          formik.values.days_until_due !== null
                            ? null
                            : 30
                        );
                      }}
                    />
                  </div>
                  <Field
                    type="number"
                    name="days_until_due"
                    formik={formik}
                    disabled={formik.values.days_until_due === null}
                  />     
              </div>
            </div>
          )}
          <div>
            <h3 className="mr-2">{tr("Settings")}</h3>
            <div className="flex space-x-2 items-center">
              <Switch
                checked={formik.values.total_only}
                onChange={(e) => {
                  formik.setFieldValue("total_only", e);
                }}
              />
              <label>{tr("Show Total Only")}</label>
            </div>
          </div>
          {!formik.values.total_only && (
            <>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="products"
                  checked={formik.values.products}
                  onChange={formik.handleChange}
                />
                <StyledLabel>
                  {tr("Show Parts")}
                  <span className="text-gray-500 text-[13px]">
                    {" "}
                    (
                    {tr(
                      "If unselected will show summary of parts if any exist"
                    )}
                    )
                  </span>
                </StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="services"
                  checked={formik.values.services}
                  onChange={formik.handleChange}
                />
                <StyledLabel>
                  {tr("Show Services")}
                  <span className="text-gray-500 text-[13px]">
                    {" "}
                    (
                    {tr(
                      "If unselected will show summary of services if any exist"
                    )}
                    )
                  </span>
                </StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="name"
                  checked={formik.values.name}
                  onChange={formik.handleChange}
                />
                <StyledLabel>{tr("Line Item Name")}</StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="long_description"
                  checked={formik.values.long_description}
                  onChange={formik.handleChange}
                />
                <StyledLabel>{tr("Line Item Long Description")}</StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="short_description"
                  checked={formik.values.short_description}
                  onChange={formik.handleChange}
                />
                <StyledLabel>{tr("Line Item Short Description")}</StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="include_images"
                  checked={formik.values.include_images}
                  onChange={formik.handleChange}
                />
                <StyledLabel>{tr("Line Item Images")}</StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="include_qty"
                  checked={formik.values.include_qty}
                  onChange={formik.handleChange}
                />
                <StyledLabel>{tr("Show Line Item Rate")}</StyledLabel>
              </div>
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  name="include_line_total"
                  checked={formik.values.include_line_total}
                  onChange={formik.handleChange}
                />
                <StyledLabel>{tr("Show Line Item Total")}</StyledLabel>
              </div>
            </>
          )}
          <div className="flex items-center">
            <h3>{tr("Color")}</h3>
            <div className="ml-2 text-gray-500 text-[13px]">
              ({tr("Select a color that best represents your company")})
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <input
              type="checkbox"
              name="color"
              checked={formik.values.color === null}
              onChange={handleDefaultColorSelection}
            />
            <StyledLabel>
              {tr("Default")}
              {/* {colorMsg && (
                <span className="text-red-500 ml-2">
                  Please select a color
                </span>
              )} */}
            </StyledLabel>
          </div>
          <div>
            <input
              type="input"
              name="colorHex"
              className="border border-gray-300 rounded-md w-20 indent-1 text-gray-600"
              maxLength={7}
              placeholder={tr("Color Hex")}
              value={formik.values.color ?? ""}
              onChange={(e) => {
                formik.setFieldValue("color", e.target.value);
                setLastColor(e.target.value);
                setColorMsg("");
              }}
            />
          </div>
          <SliderPicker
            color={formik.values.color ?? undefined}
            onChange={handleColorChange}
          />
          <div>
            <div>
              <h3>{tr("Assignees")}</h3>
            </div>
            <div>
              <Assignee
                value={query.data?.assignees}
                onChange={(e: any) => {}}
              />
            </div>
          </div>
          {/* Removed Checklists for now, not really needed */}
          {/* <div className="space-y-2">
            <div className="flex items-center">
              <h3>{tr("Checklists")}</h3>
              <SubtleButton
                onClick={() =>
                  formik.setFieldValue(
                    "checklists",
                    formik.values.checklists
                      ? [...formik.values.checklists, { id: "new", label: "" }]
                      : [{ id: "new", label: "" }]
                  )
                }
              >
                <RiAddCircleLine className="text-primary rounded-full hover:bg-gray-100 active:bg-gray-200" />
              </SubtleButton>
            </div>
            {populateChecklists}
            <div>
            </div>
          </div> */}

          <div className="space-y-2">
            <div className="flex items-center">
              <h3>{tr("Terms And Conditions")}</h3>
              {formik.values.type !== "invoice" && (
              <button
                type="button"
                className="ml-2 text-primary rounded-full hover:text-white hover:bg-primary active:bg-gray-200"
                onClick={() =>
                  formik.setFieldValue(
                    "additional_terms",
                    formik.values.additional_terms
                      ? [...formik.values.additional_terms, { id: "new", label: "", text: "", type: "none" }]
                      : [{ id: "new", label: "", text: "", type: "none" }]
                  )
                }
              >
                <RiAddCircleLine size={24} />
              </button>
              )}
            </div>
            {formik.values.type !== "invoice" && populateAdditionalTerms}
            <Field
              type="textarea"
              name="terms_conditions"
              label={formik.values.type !== "invoice" ? tr("Terms And Conditions") : ""}
              minRows={10}
              formik={formik}
            />
          </div>

        </form>
      </SafeArea>
    </div>
  );
}
